import { mat4 } from '../math/mat4'
import { quat } from '../math/quat'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps } from '../math/pose'
import { snapToRay, type CameraModel } from '../camera/model'
import type { FaceFrame } from '../tracking/types'
import { FACE_LM } from '../tracking/landmarks'
import type { AnchorSpec } from '../product/schema'
import type { FaceAnchorResult } from './face-anchor'
import { FACE_ANCHORS } from './face-anchors'

export interface EarringsSolveResult {
  left: FaceAnchorResult
  right: FaceAnchorResult
  /** Weight the observed (ear-tracker) lobe point received per side this frame (0 = mesh-edge fallback). */
  observedWeight: { left: number; right: number }
}

/** A detected ear-lobe attachment point from an external ear tracker (normalized image coords). */
export interface ObservedEar {
  x: number
  y: number
  /** 0..1 — below `minConfidence` the mesh-edge fallback is used; ramps to full trust at `fullConfidence`. */
  confidence: number
}

export interface ObservedEars {
  left?: ObservedEar | null
  right?: ObservedEar | null
}

export const OBSERVED_EAR_MIN_CONFIDENCE = 0.35
export const OBSERVED_EAR_FULL_CONFIDENCE = 0.75

/**
 * Canonical mesh-edge points in front of the ears (vertices 454 / 234). These are the ONLY
 * ear-adjacent geometry the face mesh has, and their observed 2-D positions track the real
 * ear-front silhouette on any face — unlike the canonical head, which assumes average anatomy,
 * and unlike BlazePose's ear keypoints, which are biased inward toward the cheek.
 */
const EDGE: Record<'left' | 'right', { point: Vec3; landmark: number }> = {
  left: { point: [7.66, 0.67, -2.44], landmark: FACE_LM.LEFT_EAR },
  right: { point: [-7.66, 0.67, -2.44], landmark: FACE_LM.RIGHT_EAR },
}

const _edgeCam: Vec3 = [0, 0, 0]
const _offset: Vec3 = [0, 0, 0]
const _userOffset: Vec3 = [0, 0, 0]
const _obsCam: Vec3 = [0, 0, 0]
const _userRot: Vec3 = [0, 0, 0]
const clampW = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

/**
 * Both-ear solver, accurate per-face:
 *  1. mesh-edge point 454/234 → camera space via the head matrix (depth),
 *  2. snapped to the ray of its OBSERVED landmark (screen position = this face's ear-front),
 *  3. + canonical edge→lobe offset and the user's fitting offset, rotated into the head frame.
 * The spec is authored for the LEFT lobe; the right side mirrors offset.x and rotation.y/z.
 */
export function solveEarrings(
  frame: FaceFrame,
  camera: CameraModel,
  spec: AnchorSpec,
  observed?: ObservedEars | null,
  out?: EarringsSolveResult,
): EarringsSolveResult {
  const res: EarringsSolveResult =
    out ?? {
      left: { pose: poseOps.create(), local: poseOps.create(), head: poseOps.create() },
      right: { pose: poseOps.create(), local: poseOps.create(), head: poseOps.create() },
      observedWeight: { left: 0, right: 0 },
    }

  for (const side of ['left', 'right'] as const) {
    const r = res[side]
    poseOps.fromMat4(r.head, frame.headMatrix)
    r.head.scale = 1

    const edge = EDGE[side]
    mat4.transformPoint(_edgeCam, frame.headMatrix, edge.point)
    if (edge.landmark * 3 + 1 < frame.landmarks.length) {
      snapToRay(_edgeCam, camera, _edgeCam, frame.landmarks[edge.landmark * 3]!, frame.landmarks[edge.landmark * 3 + 1]!)
    }

    const lobe = FACE_ANCHORS[side === 'left' ? 'ear_lobe_left' : 'ear_lobe_right'].point
    vec3.sub(_offset, lobe, edge.point)
    _userOffset[0] = side === 'left' ? spec.offset[0] : -spec.offset[0]
    _userOffset[1] = spec.offset[1]
    _userOffset[2] = spec.offset[2]
    vec3.add(_offset, _offset, _userOffset)
    quat.rotateVec3(_offset, r.head.rotation, _offset)
    vec3.add(r.pose.position, _edgeCam, _offset)

    // Observed lobe from the ear tracker: our own model DETECTS the lobe attachment point,
    // so when confident we snap the anchor's screen position straight onto it (depth kept),
    // then re-apply only the user's fitting offset. Blended by confidence so fallback is seamless.
    const obs = side === 'left' ? observed?.left : observed?.right
    let w = 0
    if (obs) {
      w = clampW((obs.confidence - OBSERVED_EAR_MIN_CONFIDENCE) / (OBSERVED_EAR_FULL_CONFIDENCE - OBSERVED_EAR_MIN_CONFIDENCE))
      if (w > 0) {
        snapToRay(_obsCam, camera, r.pose.position, obs.x, obs.y)
        quat.rotateVec3(_userRot, r.head.rotation, _userOffset)
        vec3.add(_obsCam, _obsCam, _userRot)
        vec3.lerp(r.pose.position, r.pose.position, _obsCam, w)
      }
    }
    res.observedWeight[side] = w

    const ry = side === 'left' ? spec.rotation[1] : -spec.rotation[1]
    const rz = side === 'left' ? spec.rotation[2] : -spec.rotation[2]
    quat.fromEulerDeg(r.local.rotation, spec.rotation[0], ry, rz)
    quat.multiply(r.pose.rotation, r.head.rotation, r.local.rotation)
    quat.normalize(r.pose.rotation, r.pose.rotation)
    r.pose.scale = spec.scale
    vec3.copy(r.local.position, _offset)
    r.local.scale = spec.scale
  }
  return res
}
